import Layout from "@/components/Layout";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import PermissionDenied from "@/components/PermissionDenied";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { useGridPagination } from "@/hooks/useGridPagination";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import {
  createHoliday,
  deleteHoliday,
  fetchWorkingCalendar,
  getWorkingCalendarCached,
  listCalendarHolidays,
  syncHolidaysFromApi,
  updateHoliday,
  updateWorkingCalendar,
  workingCalendarApiErrorMessage,
  type CalendarHolidayRecord,
  type Weekday,
} from "@/lib/hr/workingCalendarStore";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { CalendarIcon, Edit, Filter, LayoutGrid, List, Loader2, Plus, Save, Search, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

const WEEKDAYS: Array<{ id: Weekday; label: string; short: string }> = [
  { id: 0, label: "Sunday", short: "Sun" },
  { id: 1, label: "Monday", short: "Mon" },
  { id: 2, label: "Tuesday", short: "Tue" },
  { id: 3, label: "Wednesday", short: "Wed" },
  { id: 4, label: "Thursday", short: "Thu" },
  { id: 5, label: "Friday", short: "Fri" },
  { id: 6, label: "Saturday", short: "Sat" },
];

export default function WorkingCalendar() {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canUpdateWeeklyOffs = can(user, "working_calendar_can_update_weekly_offs");
  const canCreateHoliday = can(user, "working_calendar_holiday_can_create");
  const canUpdateHoliday = can(user, "working_calendar_holiday_can_update");
  const canDeleteHoliday = can(user, "working_calendar_holiday_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  const [bootLoading, setBootLoading] = useState(true);
  const [savingWeekly, setSavingWeekly] = useState(false);
  const [weeklyOffs, setWeeklyOffs] = useState<Weekday[]>([]);

  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);
  const [holidayEditing, setHolidayEditing] = useState<CalendarHolidayRecord | null>(null);
  const [holidayTitle, setHolidayTitle] = useState("");
  const [holidayStart, setHolidayStart] = useState("");
  const [holidayEnd, setHolidayEnd] = useState("");
  const [holidayNotes, setHolidayNotes] = useState("");
  const [holidaySaving, setHolidaySaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CalendarHolidayRecord | null>(null);
  const [deleting, setDeleting] = useState(false);

  const calendar = useMemo(() => getWorkingCalendarCached(), [version]);
  const holidays = useMemo(() => listCalendarHolidays(), [version]);

  const [holidayQuery, setHolidayQuery] = useState("");
  const [holidayFiltersOpen, setHolidayFiltersOpen] = useState(false);
  const [filterFrom, setFilterFrom] = useState<Date | undefined>(undefined);
  const [filterTo, setFilterTo] = useState<Date | undefined>(undefined);
  const [holidayQuickDate, setHolidayQuickDate] = useState<"all" | "today" | "week" | "month">("all");
  const [holidaySortBy, setHolidaySortBy] = useState<"latest" | "oldest" | "title_asc" | "title_desc">("latest");
  const [holidayViewMode, setHolidayViewMode] = useState<"grid" | "list">("grid");

  useEffect(() => {
    let mounted = true;
    const boot = async () => {
      setBootLoading(true);
      setIsForbidden(false);
      try {
        await fetchWorkingCalendar();
        await syncHolidaysFromApi();
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 403) {
          if (mounted) setIsForbidden(true);
          return;
        }
        console.error(e);
        toast.error(workingCalendarApiErrorMessage(e));
      } finally {
        if (mounted) {
          refresh();
          setBootLoading(false);
        }
      }
    };
    void boot();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    if (!calendar) return;
    setWeeklyOffs(calendar.weeklyOffs ?? []);
  }, [calendar?.id, calendar?.weeklyOffs]);

  const toggleWeeklyOff = (d: Weekday) => {
    setWeeklyOffs((prev) => {
      const has = prev.includes(d);
      const next = has ? prev.filter((x) => x !== d) : [...prev, d];
      return next.slice().sort((a, b) => a - b) as Weekday[];
    });
  };

  const saveWeekly = async () => {
    if (savingWeekly) return;
    setSavingWeekly(true);
    try {
      const result = await updateWorkingCalendar({ weeklyOffs });
      toast.success(result.message);
      refresh();
    } catch (e) {
      console.error(e);
      toast.error(workingCalendarApiErrorMessage(e));
    } finally {
      setSavingWeekly(false);
    }
  };

  const openCreateHoliday = () => {
    setHolidayEditing(null);
    setHolidayTitle("");
    setHolidayStart("");
    setHolidayEnd("");
    setHolidayNotes("");
    setHolidayDialogOpen(true);
  };

  const openEditHoliday = (h: CalendarHolidayRecord) => {
    setHolidayEditing(h);
    setHolidayTitle(h.title);
    setHolidayStart(h.startDate);
    setHolidayEnd(h.endDate);
    setHolidayNotes(h.notes);
    setHolidayDialogOpen(true);
  };

  const closeHolidayDialog = () => {
    setHolidayDialogOpen(false);
    setHolidayEditing(null);
    setHolidayTitle("");
    setHolidayStart("");
    setHolidayEnd("");
    setHolidayNotes("");
  };

  const saveHoliday = async () => {
    if (holidaySaving) return;
    if (!holidayTitle.trim()) {
      toast.error("Title is required.");
      return;
    }
    if (!holidayStart.trim()) {
      toast.error("Start date is required.");
      return;
    }
    if (holidayEnd.trim() && holidayEnd.trim() < holidayStart.trim()) {
      toast.error("End date must be on or after start date.");
      return;
    }
    setHolidaySaving(true);
    try {
      if (holidayEditing) {
        const res = await updateHoliday(holidayEditing.id, {
          title: holidayTitle,
          startDate: holidayStart,
          endDate: holidayEnd,
          notes: holidayNotes,
        });
        if (!res) {
          toast.error("Holiday not found.");
          return;
        }
        toast.success(res.message);
      } else {
        const res = await createHoliday({
          title: holidayTitle,
          startDate: holidayStart,
          endDate: holidayEnd,
          notes: holidayNotes,
        });
        toast.success(res.message);
      }
      closeHolidayDialog();
      await syncHolidaysFromApi();
      refresh();
    } catch (e) {
      console.error(e);
      toast.error(workingCalendarApiErrorMessage(e));
    } finally {
      setHolidaySaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      const res = await deleteHoliday(deleteTarget.id);
      toast.success(res.message);
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      console.error(e);
      toast.error(workingCalendarApiErrorMessage(e));
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (holidayQuickDate === "all") return;
    const now = new Date();
    const end = endOfDay(now);
    const start =
      holidayQuickDate === "today"
        ? startOfDay(now)
        : holidayQuickDate === "week"
          ? startOfDay(subDays(now, 6))
          : startOfMonth(now);
    setFilterFrom(start);
    setFilterTo(end);
  }, [holidayQuickDate]);

  const filteredHolidays = useMemo(() => {
    const q = holidayQuery.trim().toLowerCase();
    const fromIso = filterFrom ? format(filterFrom, "yyyy-MM-dd") : "";
    const toIso = filterTo ? format(filterTo, "yyyy-MM-dd") : "";

    return holidays.filter((h) => {
      if (q) {
        const hay = `${h.title} ${h.notes} ${h.startDate} ${h.endDate}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (fromIso) {
        const end = (h.endDate || h.startDate || "").slice(0, 10);
        if (end && end < fromIso) return false;
      }
      if (toIso) {
        const start = (h.startDate || "").slice(0, 10);
        if (start && start > toIso) return false;
      }
      return true;
    });
  }, [holidays, holidayQuery, filterFrom, filterTo]);

  const sortedHolidays = useMemo(() => {
    const items = [...filteredHolidays];
    items.sort((a, b) => {
      switch (holidaySortBy) {
        case "oldest":
          return (a.startDate || "").localeCompare(b.startDate || "") || a.id.localeCompare(b.id);
        case "title_desc":
          return (b.title || "").localeCompare(a.title || "") || (b.startDate || "").localeCompare(a.startDate || "");
        case "title_asc":
          return (a.title || "").localeCompare(b.title || "") || (b.startDate || "").localeCompare(a.startDate || "");
        case "latest":
        default:
          return (b.startDate || "").localeCompare(a.startDate || "") || b.id.localeCompare(a.id);
      }
    });
    return items;
  }, [filteredHolidays, holidaySortBy]);

  const holidayFiltersCount = (filterFrom ? 1 : 0) + (filterTo ? 1 : 0);
  const holidayPaginationKey = `${holidayQuery}|${holidaySortBy}|${filterFrom?.toISOString() ?? ""}|${filterTo?.toISOString() ?? ""}`;
  const {
    page: holidayPage,
    setPage: setHolidayPage,
    pageSize: holidayPageSize,
    setPageSize: setHolidayPageSize,
    totalCount: holidayTotalCount,
    totalPages: holidayTotalPages,
    paginatedItems: paginatedHolidays,
    rangeStart: holidayRangeStart,
    rangeEnd: holidayRangeEnd,
  } = useGridPagination(sortedHolidays, holidayPaginationKey);

  return isForbidden ? (
    <PermissionDenied
      title="Working calendar access required"
      message="You don’t have permission to view working calendar. Ask an administrator to grant working_calendar_can_view_any."
    />
  ) : (
    <Layout title="Working calendar">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Working calendar</h1>
            <p className="text-sm text-muted-foreground mt-1">Weekly offs and holidays for attendance rules.</p>
          </div>
          <div className="flex gap-2">
            {canCreateHoliday ? (
              <Button variant="glass" className="gap-2" onClick={openCreateHoliday}>
                <Plus className="w-4 h-4" />
                Add holiday
              </Button>
            ) : null}
            {canUpdateWeeklyOffs ? (
              <Button variant="premium" className="gap-2" onClick={() => void saveWeekly()} disabled={savingWeekly}>
                {savingWeekly ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save weekly offs
              </Button>
            ) : null}
          </div>
        </div>

        {bootLoading ? (
          <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading working calendar…
          </div>
        ) : null}

        <Tabs defaultValue="weekly" className="w-full">
          <TabsList className="w-full justify-start overflow-x-auto">
            <TabsTrigger value="weekly" className="shrink-0">
              Weekly offs
            </TabsTrigger>
            <TabsTrigger value="holidays" className="shrink-0">
              Holidays
            </TabsTrigger>
          </TabsList>

          <TabsContent value="weekly" className="mt-4">
            <div className="glass-card p-6 space-y-4">
              <div>
                <p className="text-sm font-semibold text-foreground">Weekly offs</p>
                <p className="text-[11px] text-muted-foreground mt-1">Select which weekdays are non-working.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map((d) => {
                  const active = weeklyOffs.includes(d.id);
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => toggleWeeklyOff(d.id)}
                      className={cn(
                        "h-10 px-3 rounded-lg border text-sm font-medium transition-colors",
                        active
                          ? "border-primary/25 bg-primary/10 text-foreground"
                          : "border-border/60 bg-background/60 text-muted-foreground hover:bg-muted/30",
                      )}
                    >
                      <span className="sm:hidden">{d.short}</span>
                      <span className="hidden sm:inline">{d.label}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Current:{" "}
                {weeklyOffs.length
                  ? weeklyOffs.map((x) => WEEKDAYS.find((d) => d.id === x)?.label ?? String(x)).join(", ")
                  : "None"}
              </p>
            </div>
          </TabsContent>

          <TabsContent value="holidays" className="mt-4">
            <div className="glass-card p-6">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1 min-w-0">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by title, notes, or dates…"
                      className="glass-input pl-9 h-10"
                      value={holidayQuery}
                      onChange={(e) => setHolidayQuery(e.target.value)}
                    />
                  </div>

                  <Sheet open={holidayFiltersOpen} onOpenChange={setHolidayFiltersOpen}>
                    <SheetTrigger asChild>
                      <Button variant="glass" className="gap-2 shrink-0">
                        <Filter className="w-4 h-4" />
                        Filters
                        {holidayFiltersCount > 0 ? (
                          <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                            {holidayFiltersCount}
                          </span>
                        ) : null}
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                      <SheetHeader>
                        <SheetTitle>Filter holidays</SheetTitle>
                        <SheetDescription>Date range.</SheetDescription>
                      </SheetHeader>

                      <div className="mt-6 space-y-5">
                        <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                          <p className="text-sm font-medium text-foreground">Date range</p>

                          <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">From</Label>
                            <Popover modal={true}>
                              <PopoverTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className={cn(
                                    "glass-input h-10 w-full justify-start text-left font-normal",
                                    !filterFrom && "text-muted-foreground",
                                  )}
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                                  {filterFrom ? format(filterFrom, "PPP") : "Any"}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                                <Calendar mode="single" selected={filterFrom} onSelect={setFilterFrom} initialFocus />
                                <div className="flex gap-2 border-t p-2">
                                  <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterFrom(undefined)}>
                                    Clear
                                  </Button>
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">To</Label>
                            <Popover modal={true}>
                              <PopoverTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className={cn(
                                    "glass-input h-10 w-full justify-start text-left font-normal",
                                    !filterTo && "text-muted-foreground",
                                  )}
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                                  {filterTo ? format(filterTo, "PPP") : "Any"}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                                <Calendar mode="single" selected={filterTo} onSelect={setFilterTo} initialFocus />
                                <div className="flex gap-2 border-t p-2">
                                  <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterTo(undefined)}>
                                    Clear
                                  </Button>
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>
                        </div>
                      </div>

                      <SheetFooter className="mt-6 gap-2">
                        <Button
                          type="button"
                          variant="glass"
                          className="w-full"
                          onClick={() => {
                            setFilterFrom(undefined);
                            setFilterTo(undefined);
                          }}
                        >
                          Reset
                        </Button>
                        <SheetClose asChild>
                          <Button type="button" variant="premium" className="w-full">
                            Apply filters
                          </Button>
                        </SheetClose>
                      </SheetFooter>
                    </SheetContent>
                  </Sheet>
                </div>

                <div className="flex items-center justify-between gap-2 md:hidden">
                  <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                      {(
                        [
                          { id: "today" as const, label: "Today", mobile: "Today" },
                          { id: "week" as const, label: "Week", mobile: "7D" },
                          { id: "month" as const, label: "Month", mobile: "30D" },
                          { id: "all" as const, label: "All", mobile: "All" },
                        ] as const
                      ).map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => {
                            setHolidayQuickDate(opt.id);
                            if (opt.id === "all") {
                              setFilterFrom(undefined);
                              setFilterTo(undefined);
                            }
                          }}
                          className={cn(
                            "h-full px-3 text-sm transition-colors",
                            holidayQuickDate === opt.id ? "bg-primary/10 text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50",
                          )}
                        >
                          <span className="sm:hidden">{opt.mobile}</span>
                          <span className="hidden sm:inline">{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                    <button
                      type="button"
                      onClick={() => setHolidayViewMode("grid")}
                      className={cn(
                        "h-full px-3 text-sm transition-colors",
                        holidayViewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                      )}
                      aria-label="Grid view"
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setHolidayViewMode("list")}
                      className={cn(
                        "h-full px-3 text-sm transition-colors",
                        holidayViewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                      )}
                      aria-label="List view"
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2 md:hidden">
                  <GridPerPageSelect
                    id="holidays-page-size-mobile"
                    pageSize={holidayPageSize}
                    onPageSizeChange={setHolidayPageSize}
                    options={PAGE_SIZE_OPTIONS}
                  />
                  <div className="w-[170px] shrink-0">
                    <Select value={holidaySortBy} onValueChange={(v) => setHolidaySortBy(v as typeof holidaySortBy)}>
                      <SelectTrigger className="glass-input h-10 w-full">
                        <SelectValue placeholder="Sort by" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="latest">Newest first</SelectItem>
                        <SelectItem value="oldest">Oldest first</SelectItem>
                        <SelectItem value="title_asc">Title: A to Z</SelectItem>
                        <SelectItem value="title_desc">Title: Z to A</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="hidden md:flex items-center justify-between gap-3">
                  <GridPerPageSelect
                    id="holidays-page-size-desktop"
                    pageSize={holidayPageSize}
                    onPageSizeChange={setHolidayPageSize}
                    options={PAGE_SIZE_OPTIONS}
                  />
                  <div className="flex items-center gap-3 justify-end min-w-0">
                    <div className="w-[200px] shrink-0">
                      <Select value={holidaySortBy} onValueChange={(v) => setHolidaySortBy(v as typeof holidaySortBy)}>
                        <SelectTrigger className="glass-input h-10 w-full">
                          <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="latest">Newest first</SelectItem>
                          <SelectItem value="oldest">Oldest first</SelectItem>
                          <SelectItem value="title_asc">Title: A to Z</SelectItem>
                          <SelectItem value="title_desc">Title: Z to A</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                      {(
                        [
                          { id: "today" as const, label: "Today" },
                          { id: "week" as const, label: "Week" },
                          { id: "month" as const, label: "Month" },
                          { id: "all" as const, label: "All" },
                        ] as const
                      ).map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => {
                            setHolidayQuickDate(opt.id);
                            if (opt.id === "all") {
                              setFilterFrom(undefined);
                              setFilterTo(undefined);
                            }
                          }}
                          className={cn(
                            "h-full px-3 text-sm transition-colors",
                            holidayQuickDate === opt.id ? "bg-primary/10 text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50",
                          )}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                      <button
                        type="button"
                        onClick={() => setHolidayViewMode("grid")}
                        className={cn(
                          "h-full px-3 text-sm transition-colors",
                          holidayViewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                        )}
                        aria-label="Grid view"
                      >
                        <LayoutGrid className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setHolidayViewMode("list")}
                        className={cn(
                          "h-full px-3 text-sm transition-colors",
                          holidayViewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                        )}
                        aria-label="List view"
                      >
                        <List className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {sortedHolidays.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-4">
                  {holidays.length === 0 ? "No holidays yet. Click “Add holiday” to create one." : "No holidays match your search."}
                </p>
              ) : holidayViewMode === "grid" ? (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  {paginatedHolidays.map((h) => {
                    const range = h.endDate?.trim() ? `${h.startDate} → ${h.endDate}` : h.startDate || "—";
                    const notes = h.notes?.trim() ? h.notes.trim() : "—";
                    return (
                      <CatalogEntityCard
                        key={h.id}
                        title={h.title || "—"}
                        titleIcon={CalendarIcon}
                        primaryMeta={{
                          label: "Date(s)",
                          value: range,
                          icon: CalendarIcon,
                          iconClassName: "text-sky-600",
                        }}
                        secondaryMeta={{
                          label: "Notes",
                          value: notes,
                          icon: Edit,
                          iconClassName: "text-emerald-600",
                        }}
                        footer={`Last updated: ${formatCatalogAuditDate(h.updatedAt)}`}
                        onEdit={canUpdateHoliday ? () => openEditHoliday(h) : undefined}
                        onDelete={canDeleteHoliday ? () => setDeleteTarget(h) : undefined}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  {paginatedHolidays.map((h) => {
                    const range = h.endDate?.trim() ? `${h.startDate} → ${h.endDate}` : h.startDate || "—";
                    return (
                      <div key={h.id} className="glass-card p-3">
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                            <CalendarIcon className="w-8 h-8 text-sky-600/50" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-sm font-semibold text-foreground truncate">{h.title || "—"}</h3>
                            <p className="text-xs text-muted-foreground mt-0.5 font-mono">{range}</p>
                            <p className="text-xs text-muted-foreground mt-1 truncate">{h.notes?.trim() ? h.notes : "—"}</p>
                          </div>
                          <div className="flex items-center gap-2 ml-auto">
                            {canUpdateHoliday ? (
                              <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => openEditHoliday(h)}>
                                <Edit className="w-3 h-3" /> Edit
                              </Button>
                            ) : null}
                            {canDeleteHoliday ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                                onClick={() => setDeleteTarget(h)}
                                aria-label="Delete holiday"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <GridPaginationFooter
                totalCount={holidayTotalCount}
                rangeStart={holidayRangeStart}
                rangeEnd={holidayRangeEnd}
                page={holidayPage}
                totalPages={holidayTotalPages}
                onPageChange={setHolidayPage}
              />
            </div>
          </TabsContent>
        </Tabs>

        <Dialog
          open={holidayDialogOpen}
          onOpenChange={(o) => {
            if (!o) closeHolidayDialog();
            else setHolidayDialogOpen(true);
          }}
        >
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{holidayEditing ? "Edit holiday" : "New holiday"}</DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-1 gap-4 py-2">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  Title <span className="text-destructive">*</span>
                </Label>
                <Input className="glass-input h-10" value={holidayTitle} onChange={(e) => setHolidayTitle(e.target.value)} placeholder="e.g. 14 August" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    Start date <span className="text-destructive">*</span>
                  </Label>
                  <DatePartsPicker
                    value={holidayStart}
                    onChange={setHolidayStart}
                    fromYear={1980}
                    toYear={new Date().getFullYear() + 5}
                    placeholder="Select start date"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">End date (optional)</Label>
                  <DatePartsPicker
                    value={holidayEnd}
                    onChange={setHolidayEnd}
                    fromYear={1980}
                    toYear={new Date().getFullYear() + 5}
                    placeholder="Select end date"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Notes</Label>
                <Textarea className="glass-input min-h-[90px]" value={holidayNotes} onChange={(e) => setHolidayNotes(e.target.value)} placeholder="Optional notes…" />
              </div>
            </div>

            <DialogFooter>
              <Button variant="glass" onClick={closeHolidayDialog} disabled={holidaySaving}>
                Cancel
              </Button>
              <Button variant="premium" onClick={() => void saveHoliday()} disabled={holidaySaving}>
                {holidaySaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this holiday?"
          description={deleteTarget ? deleteTarget.title : ""}
          mode="confirm"
          variant="danger"
          confirmLabel={deleting ? "Deleting..." : "Delete"}
          cancelLabel="Cancel"
          onConfirm={() => void confirmDelete()}
        />
      </div>
    </Layout>
  );
}

